import { WorkflowIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { WhatsAppFlowAction, WhatsAppFlowDisplay } from '@/shared/types/action';

type WhatsAppFlowContentProps = { flow?: WhatsAppFlowDisplay; fallbackMessage?: string };

/**
 * Shared component for rendering WhatsApp Flow content.
 * Used in both the action preview and message bubbles.
 */
export const WhatsAppFlowContent = ({ flow, fallbackMessage }: WhatsAppFlowContentProps) => {
  const { t } = useTranslation();
  const hasContent = flow?.flow_header || flow?.flow_body || flow?.flow_cta;
  const fallback = fallbackMessage ?? t('conversation.whatsapp_flow_sent');

  return (
    <div className="flex flex-col gap-2">
      {flow?.flow_header && <strong className="text-sm">{flow.flow_header}</strong>}
      {flow?.flow_body && <p className="text-sm">{flow.flow_body}</p>}
      {flow?.flow_footer && <p className="text-xs text-foreground-muted">{flow.flow_footer}</p>}
      {flow?.flow_cta && (
        <Button
          variant="outline-neutral"
          size="sm"
          disabled
          className="mt-1 h-auto whitespace-normal"
        >
          {flow.flow_cta}
        </Button>
      )}
      {!hasContent && (
        <div className="flex items-center gap-2">
          <WorkflowIcon className="size-4" />
          <span className="text-sm">{fallback}</span>
        </div>
      )}
    </div>
  );
};

type WhatsAppFlowProps = { action: WhatsAppFlowAction };

const WhatsAppFlow = ({ action }: WhatsAppFlowProps) => {
  const { t } = useTranslation();
  const flow = action.whatsapp_flow;

  if (!flow?.flow_id) {
    return <span className="text-sm text-foreground-muted">{t('dialog.flow.no_flow_id')}</span>;
  }

  return <WhatsAppFlowContent flow={flow} />;
};

export default WhatsAppFlow;
